import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';

import { Typography } from '@/ui';

import { LoadError } from '../PageState';
import { Toast, useToast } from '../Toast';
import { copyText } from '../clipboard';

import ReflectionV2View from './ReflectionV2View';
import {
  highlightText,
  reflectionCopyText,
  v2SourceIdentity,
} from './reflectionV2Items';

import type { ReflectionV2Item } from './reflectionV2Items';
import type {
  CreateMomentsArtifactNoteArg,
  MomentsArtifactNote,
} from '@/store/api/momentsApi';
import type { FC, ReactNode } from 'react';

import { useMcpMemberFlag } from '@/hooks/useMcpMemberFlag';
import {
  useGetCoachingNotesReflectionQuery,
  useGetMcpConnectGrantsQuery,
} from '@/store/api/mcpMemberApi';

export const MEMBER_REFLECTION_V2_PROFILE_SLUG = 'member-reflection-v2';

// NEW COPY: a denied clipboard write must not read as success.
const COPY_FAILED = 'Couldn’t copy. Try again.';

export interface ReflectionV2BranchProps {
  artifactId: string;
  notes: MomentsArtifactNote[];
  onBack: () => void;
  onCreateNote: (arg: CreateMomentsArtifactNoteArg) => Promise<unknown>;
  onDeleteNote: (id: string) => Promise<unknown>;
  onFeedback: (value: 'positive' | 'negative', text: string) => Promise<void>;
  transcriptHref: string | null;
}

interface PendingHighlight {
  basis: MomentsArtifactNote[];
  key: string;
  on: boolean;
}

// Same copy as MemberReflectionArtifactView's status states.
const Status: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    className="mx-auto max-w-[600px] px-6 py-16 text-cement-400"
    variant="body1"
  >
    {children}
  </Typography>
);

const ReflectionV2Branch: FC<ReflectionV2BranchProps> = ({
  artifactId,
  notes,
  onBack,
  onCreateNote,
  onDeleteNote,
  onFeedback,
  transcriptHref,
}) => {
  const flag = useMcpMemberFlag();
  const reflection = useGetCoachingNotesReflectionQuery(
    flag.enabled ? { artifactId } : skipToken,
  );
  const grants = useGetMcpConnectGrantsQuery(
    flag.enabled ? undefined : skipToken,
  );
  const { close, show, toast } = useToast();
  // A pending toggle shows at once and is dropped when the notes list changes.
  const [pending, setPending] = useState<PendingHighlight | null>(null);

  if (flag.isLoading || (flag.enabled && reflection.isLoading))
    return <Status>Loading reflection…</Status>;
  if (!flag.enabled) return <Status>Reflection not found.</Status>;
  if (reflection.isError || !reflection.data) {
    return (
      <LoadError
        onRetry={() => {
          reflection.refetch().catch(() => undefined);
        }}
      />
    );
  }
  const { data } = reflection;
  const aiName = grants.data?.grants[0]?.client_name ?? null;

  // Only v2 highlights of the revision on screen count (contracts §8.11). The
  // list also returns notes from older revisions, whose keys may now name
  // different text; those are neither shown as current nor deleted here.
  const currentNotes = notes.flatMap((candidate) => {
    const identity = candidate.sourceIdentity;
    return candidate.artifactId === artifactId &&
      identity?.readerContract === 'reflection-v2-item-v1' &&
      identity.revision === data.revision
      ? [{ id: candidate.id, key: identity.blockId }]
      : [];
  });
  const highlighted = new Set(currentNotes.map((candidate) => candidate.key));
  if (pending?.basis === notes) {
    if (pending.on) highlighted.add(pending.key);
    else highlighted.delete(pending.key);
  }

  const toggle = async (item: ReflectionV2Item, on: boolean) => {
    // One toggle per item until the notes list reflects it: an un-highlight
    // before the created note is listed would delete nothing.
    if (pending?.basis === notes && pending.key === item.key) return;
    setPending({ basis: notes, key: item.key, on });
    try {
      if (on) {
        const text = highlightText(item);
        // text.length is the UTF-16 length §8.11 asks for.
        await onCreateNote({
          artifactId,
          authorRole: 'member',
          blockId: item.key,
          endIndex: text.length,
          note: '',
          selectedText: text,
          sourceIdentity: v2SourceIdentity(data.revision),
          startIndex: 0,
          tags: [],
        });
      } else {
        await Promise.all(
          currentNotes
            .filter((candidate) => candidate.key === item.key)
            .map((candidate) => onDeleteNote(candidate.id)),
        );
      }
    } catch {
      setPending(null);
      show('Couldn’t save your highlight. Try again.'); // NEW COPY
    }
  };

  return (
    <>
      <ReflectionV2View
        aiName={aiName}
        highlighted={highlighted}
        showConnectPrompt={grants.data !== undefined}
        transcriptHref={transcriptHref}
        view={data}
        onBack={onBack}
        onCopyPrompt={(text) => {
          copyText(text)
            .then((copied) =>
              show(
                copied
                  ? `Copied. Paste it into ${aiName ?? 'your AI'}.`
                  : COPY_FAILED,
              ),
            )
            .catch(() => undefined);
        }}
        onCopyReflection={() => {
          copyText(reflectionCopyText(data.reflection))
            .then((copied) => show(copied ? 'Reflection copied' : COPY_FAILED))
            .catch(() => undefined);
        }}
        onFeedback={(value) => {
          onFeedback(value, '')
            .then(() => show('Thanks for the feedback'))
            .catch(() => undefined);
        }}
        onToggleHighlight={(item, on) => {
          toggle(item, on).catch(() => undefined);
        }}
      />
      <Toast toast={toast} onClose={close} />
    </>
  );
};

export default ReflectionV2Branch;
